/**
 * Hero Section - Clean, on-brand design with subtle animations
 */

import { Link } from 'react-router-dom';
import { ArrowRight, Star, Users, Sparkles } from 'lucide-react';
import { useWebsiteContentOptional } from '../../contexts/WebsiteContentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import EditableElement from '../admin/EditableElement';
import { PhoneMockup } from './FloatingMockup';

export default function Hero() {
  const content = useWebsiteContentOptional();
  const { t } = useLanguage();

  const get = (key: string, fallback: string, i18nKey?: string) => {
    const translatedFallback = i18nKey ? t(i18nKey, fallback) : fallback;
    return content?.getContent('hero', key, translatedFallback) ?? translatedFallback;
  };

  return (
    <section className="relative min-h-[90vh] overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-64 h-64 bg-accent rounded-full blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Left Column - Text Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <EditableElement section="hero" field="badge">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium text-white mb-8 animate-fade-in border border-white/20">
                <Sparkles className="w-4 h-4 text-accent" />
                {get('badge', 'The only way to see in-store As-Is deals', 'hero.badge')}
              </div>
            </EditableElement>

            {/* Headline */}
            <EditableElement section="hero" field="headline">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white animate-slide-up leading-tight">
                <span className="block">{get('headline_line1', 'Furnish for', 'hero.headlineLine1')}</span>
                <span className="block text-accent">
                  {get('headline_line2', 'Less.', 'hero.headlineLine2')}
                </span>
              </h1>
            </EditableElement>

            {/* Subheadline */}
            <EditableElement section="hero" field="subheadline">
              <p className="mt-6 text-lg sm:text-xl text-primary-100 max-w-xl mx-auto lg:mx-0 animate-slide-up animation-delay-100">
                {get('subheadline', 'Create project checklists. Get notified when your items hit As-Is or go on sale. Save 50-70% on IKEA furniture.', 'hero.subheadline')}
              </p>
            </EditableElement>

            {/* CTA buttons */}
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 animate-slide-up animation-delay-200">
              <EditableElement section="hero" field="cta_primary">
                <Link
                  to="/signup"
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent hover:bg-accent-dark text-white font-bold rounded-2xl hover:shadow-lg hover:shadow-accent/25 transition-all duration-300 hover:-translate-y-0.5"
                >
                  {get('cta_primary', 'Get Started Free', 'hero.ctaPrimary')}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </EditableElement>
              <EditableElement section="hero" field="cta_secondary">
                <a
                  href="#features"
                  className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300"
                >
                  {get('cta_secondary', 'See How It Works', 'hero.ctaSecondary')}
                </a>
              </EditableElement>
            </div>

            {/* Trust Badges */}
            <div className="mt-12 flex flex-wrap items-center justify-center lg:justify-start gap-6 animate-fade-in animation-delay-300">
              <EditableElement section="hero" field="trust_rating">
                <div className="flex items-center gap-2 text-white/90">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-accent fill-accent" />
                    ))}
                  </div>
                  <span className="font-semibold">{get('trust_rating', '4.8')}</span>
                  <span className="text-white/60">{get('trust_rating_label', 'rating', 'hero.trustRatingLabel')}</span>
                </div>
              </EditableElement>

              <div className="w-px h-8 bg-white/20 hidden sm:block" />

              <EditableElement section="hero" field="trust_users">
                <div className="flex items-center gap-2 text-white/90">
                  <Users className="w-5 h-5 text-primary-200" />
                  <span className="font-semibold">{get('trust_users', '10K+')}</span>
                  <span className="text-white/60">{get('trust_users_label', 'happy users', 'hero.trustUsersLabel')}</span>
                </div>
              </EditableElement>
            </div>
          </div>

          {/* Right Column - Phone Mockup */}
          <div className="relative flex justify-center lg:justify-end animate-fade-in animation-delay-400">
            <div className="relative">
              {/* Subtle glow effect behind phone */}
              <div className="absolute inset-0 bg-accent/20 rounded-full blur-3xl scale-110" />

              {/* Phone mockup */}
              <PhoneMockup className="relative z-10" />
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="mt-20 grid grid-cols-3 gap-4 max-w-2xl mx-auto lg:mx-0 animate-fade-in animation-delay-500">
          <EditableElement section="hero" field="stat_countries">
            <div className="text-center p-4 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
              <div className="text-3xl sm:text-4xl font-bold text-white">{get('stat_countries', '50+')}</div>
              <div className="text-sm text-primary-200 mt-1">{get('stat_countries_label', 'Countries', 'hero.statCountriesLabel')}</div>
            </div>
          </EditableElement>
          <EditableElement section="hero" field="stat_products">
            <div className="text-center p-4 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
              <div className="text-3xl sm:text-4xl font-bold text-white">{get('stat_products', '100K+')}</div>
              <div className="text-sm text-primary-200 mt-1">{get('stat_products_label', 'As-Is Items Found', 'hero.statProductsLabel')}</div>
            </div>
          </EditableElement>
          <EditableElement section="hero" field="stat_alerts">
            <div className="text-center p-4 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
              <div className="text-3xl sm:text-4xl font-bold text-white">{get('stat_alerts', '1M+')}</div>
              <div className="text-sm text-primary-200 mt-1">{get('stat_alerts_label', 'Deal Alerts Sent', 'hero.statAlertsLabel')}</div>
            </div>
          </EditableElement>
        </div>
      </div>

      {/* Wave divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
          <path
            d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
            fill="white"
          />
        </svg>
      </div>
    </section>
  );
}
